import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  BarChart3,
  ArrowLeft,
  Loader2,
  Building2,
  Users,
  ClipboardList,
  TrendingUp,
  Activity,
  Clock,
  CheckCircle2,
  AlertTriangle
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Layout from '../../components/Layout';
import api from '../../lib/api';

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [trends, setTrends] = useState([]);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const [statsRes, companiesRes, trendsRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/admin/companies'),
        api.get('/admin/analytics/trends')
      ]);
      setStats(statsRes.data);
      setCompanies(companiesRes.data);
      setTrends(trendsRes.data.trends || []);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  // Redirect non-super-admins
  if (user?.role !== 'super_admin') {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-red-400">Access denied. Super Admin only.</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-['Barlow_Condensed'] text-3xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
                <BarChart3 className="w-8 h-8 text-amber-500" />
                Platform Analytics
              </h1>
              <p className="text-slate-400">Overview of all platform activity and usage</p>
            </div>
          </div>
        </div>

        {/* Main Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card-industrial p-6">
            <div className="flex items-center justify-between mb-4">
              <ClipboardList className="w-8 h-8 text-cyan-500" />
              <TrendingUp className="w-5 h-5 text-green-400" />
            </div>
            <p className="text-slate-400 text-sm">Total Work Orders</p>
            <p className="text-4xl font-bold text-white">{stats?.total_work_orders || 0}</p>
            <p className="text-slate-500 text-sm mt-2">across all companies</p>
          </div>

          <div className="card-industrial p-6">
            <div className="flex items-center justify-between mb-4">
              <Building2 className="w-8 h-8 text-amber-500" />
              <CheckCircle2 className="w-5 h-5 text-green-400" />
            </div>
            <p className="text-slate-400 text-sm">Active Companies</p>
            <p className="text-4xl font-bold text-green-400">{stats?.companies?.active || 0}</p>
            <p className="text-slate-500 text-sm mt-2">not suspended</p>
          </div>

          <div className="card-industrial p-6">
            <div className="flex items-center justify-between mb-4">
              <Clock className="w-8 h-8 text-amber-500" />
              <AlertTriangle className="w-5 h-5 text-amber-400" />
            </div>
            <p className="text-slate-400 text-sm">Trial Companies</p>
            <p className="text-4xl font-bold text-amber-400">{stats?.companies?.trial || 0}</p>
            <p className="text-slate-500 text-sm mt-2">in trial period</p>
          </div>

          <div className="card-industrial p-6">
            <div className="flex items-center justify-between mb-4">
              <Users className="w-8 h-8 text-purple-500" />
              <Activity className="w-5 h-5 text-purple-400" />
            </div>
            <p className="text-slate-400 text-sm">Total Users</p>
            <p className="text-4xl font-bold text-purple-400">{stats?.users?.total || 0}</p>
            <p className="text-slate-500 text-sm mt-2">{stats?.users?.admins || 0} admins</p>
          </div>
        </div>

        {/* Growth Trends Chart */}
        <div className="card-industrial p-6">
          <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-cyan-500" />
            Platform Growth Trends
          </h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis 
                  dataKey="month" 
                  stroke="#94a3b8"
                  style={{ fontSize: '12px' }}
                />
                <YAxis 
                  yAxisId="left"
                  stroke="#94a3b8"
                  style={{ fontSize: '12px' }}
                />
                <YAxis 
                  yAxisId="right"
                  orientation="right"
                  stroke="#94a3b8"
                  style={{ fontSize: '12px' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1e293b', 
                    border: '1px solid #334155',
                    borderRadius: '8px'
                  }}
                  labelStyle={{ color: '#f1f5f9' }}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="line"
                />
                <Line 
                  yAxisId="left"
                  type="monotone" 
                  dataKey="companies" 
                  stroke="#f59e0b" 
                  strokeWidth={3}
                  name="Companies"
                  dot={{ fill: '#f59e0b', r: 4 }}
                  activeDot={{ r: 6 }}
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="work_orders" 
                  stroke="#06b6d4" 
                  strokeWidth={3}
                  name="Work Orders"
                  dot={{ fill: '#06b6d4', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Company Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card-industrial p-6">
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-amber-500" />
              Companies Overview
            </h2>
            <div className="space-y-4">
              {companies.slice(0, 5).map(company => (
                <div 
                  key={company.id}
                  onClick={() => navigate('/super-admin')}
                  className="bg-slate-900 p-4 rounded cursor-pointer hover:bg-slate-800 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium">{company.name}</p>
                      <p className="text-slate-500 text-sm">
                        {company.user_count || 0} users · {company.task_count || 0} work orders
                      </p>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded uppercase ${
                      company.plan === 'enterprise' ? 'bg-green-500/20 text-green-400' :
                      company.plan === 'pro' ? 'bg-purple-500/20 text-purple-400' :
                      company.plan === 'basic' ? 'bg-blue-500/20 text-blue-400' :
                      company.plan === 'trial' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-slate-500/20 text-slate-400'
                    }`}>
                      {company.plan}
                    </span>
                  </div>
                </div>
              ))}
              {companies.length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No companies yet</p>
                </div>
              )}
            </div>
            {companies.length > 5 && (
              <Button
                variant="outline"
                onClick={() => navigate('/super-admin')}
                className="w-full mt-4 btn-secondary"
              >
                View All {companies.length} Companies
              </Button>
            )}
          </div>

          <div className="card-industrial p-6">
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-amber-500" />
              Platform Health
            </h2>
            <div className="space-y-4">
              <div className="bg-slate-900 p-4 rounded">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-400">Company Utilization</span>
                  <span className="text-green-400 font-bold">
                    {stats?.companies?.total > 0 
                      ? Math.round((stats?.companies?.active / stats?.companies?.total) * 100) 
                      : 0}%
                  </span>
                </div>
                <div className="bg-slate-700 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full"
                    style={{ width: `${stats?.companies?.total > 0 ? (stats?.companies?.active / stats?.companies?.total) * 100 : 0}%` }}
                  />
                </div>
              </div>

              <div className="bg-slate-900 p-4 rounded">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-400">Trial Conversion Potential</span>
                  <span className="text-amber-400 font-bold">
                    {stats?.companies?.trial || 0} companies
                  </span>
                </div>
                <p className="text-slate-500 text-sm">Companies that could convert to paid plans</p>
              </div>

              <div className="bg-slate-900 p-4 rounded">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-400">Avg Work Orders per Company</span>
                  <span className="text-cyan-400 font-bold">
                    {stats?.companies?.total > 0 
                      ? Math.round(stats?.total_work_orders / stats?.companies?.total) 
                      : 0}
                  </span>
                </div>
              </div>

              <div className="bg-slate-900 p-4 rounded">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-400">Avg Users per Company</span>
                  <span className="text-purple-400 font-bold">
                    {stats?.companies?.total > 0 
                      ? Math.round(stats?.users?.total / stats?.companies?.total) 
                      : 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-4">
          <Button
            onClick={() => navigate('/super-admin')}
            className="btn-primary"
          >
            <Building2 className="w-4 h-4 mr-2" />
            Manage Companies
          </Button>
          <Button
            onClick={() => navigate('/super-admin/users')}
            className="btn-secondary"
          >
            <Users className="w-4 h-4 mr-2" />
            View All Users
          </Button>
          <Button
            onClick={() => navigate('/super-admin/revenue')}
            className="btn-secondary"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Revenue Analytics
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default AnalyticsPage;
